import { APP_NONCE } from '@/config';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import referralApiService from '@/request/referral/referral.api.service';
import { useAppStore } from '@/store';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useLogin = () => {
  const { setProfile } = useAppStore();
  const { walletAddress, privyUserId, email, signMessage, wallet, chain } = useBoomWallet();
  const [searchParams] = useSearchParams();
  const referrer_code = searchParams.get('inviteCode');
  const telegram_id = searchParams.get('telegramId');
  const account = walletAddress?.toLocaleLowerCase();
  const { subscribe } = useAppEventBusContext();
  const hasTriedLogin = useRef(false);

  const privyAuthLogin = useCallback(async () => {
    try {
      if (!walletAddress || !account || wallet == null) return false;
      const address = chain == 'solana' ? walletAddress : walletAddress.toLocaleLowerCase();
      const deadline = dayjs().add(1, 'day').unix();
      const messageBytes = `${APP_NONCE} ${address}.[${deadline}]`;
      const signature = (await signMessage(messageBytes)) as string;
      const res = await referralApiService.emailLogin({
        chain_symbol: chain,
        account: walletAddress,
        deadline,
        signature,
        ...(privyUserId && { privy_uid: privyUserId }),
        ...(email && { privy_email: email }),
        ...(telegram_id && { telegram_id }),
        ...(referrer_code && { referrer_code }),
      });

      if (res.jwt_token) {
        localStorage.setItem(account, res.jwt_token);
        setProfile(res.user_info);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [walletAddress, privyUserId, email, account, wallet, referrer_code, telegram_id]);

  const bindInviteCode = useCallback(async () => {
    try {
      if (!walletAddress) return;
      const res: any = await referralApiService.getUserInfo();
      if (res?.code === 401 || res.code === 404) {
        await privyAuthLogin();
        return;
      }
      if (res.referrer_id === 0 && referrer_code) {
        await referralApiService.bindUser({ referrer_code });
      }
      setProfile(res);
    } catch (error) {
      console.log(error);
    }
  }, [walletAddress, referrer_code, setProfile, privyAuthLogin]);

  useEffect(() => {
    // 只有当钱包地址存在且尚未尝试登录时才执行
    if (walletAddress && wallet && !hasTriedLogin.current) {
      hasTriedLogin.current = true;
      bindInviteCode();
    }
  }, [walletAddress, wallet, bindInviteCode]);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Sign_Expired, (state: boolean) => {
      if (state) {
        privyAuthLogin();
      }
    });
    return () => unsubscribe();
  }, [subscribe, privyAuthLogin]);
};
